import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  Divider,
  Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  Restaurant as RestaurantIcon, 
  Store as StoreIcon,
  ShoppingBag as BagIcon,
  LocalShipping as ShippingIcon, 
  Home as HomeIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

// Estados de pedido
export const ORDER_STATUSES = {
  PENDING: { value: 'pending', label: 'Pendiente', color: 'warning' },
  CONFIRMED: { value: 'confirmed', label: 'Confirmado', color: 'info' },
  IN_PREPARATION: { value: 'in_preparation', label: 'En preparación', color: 'secondary' },
  READY: { value: 'ready', label: 'Listo', color: 'success' },
  IN_DELIVERY: { value: 'in_delivery', label: 'En reparto', color: 'primary' },
  DELIVERED: { value: 'delivered', label: 'Entregado', color: 'default' },
  COMPLETED: { value: 'completed', label: 'Completado', color: 'default' },
  CANCELLED: { value: 'cancelled', label: 'Cancelado', color: 'error' }
};

// Tipos de pedido - Incluye los íconos
export const ORDER_TYPES = {
  LOCAL: { value: 'local', label: 'En mesa', icon: <RestaurantIcon /> },
  TAKEAWAY: { value: 'takeaway', label: 'Para recoger', icon: <BagIcon /> },
  DELIVERY: { value: 'delivery', label: 'A domicilio', icon: <ShippingIcon /> }
};

// Estados de pago
export const PAYMENT_STATUSES = {
  PENDING: 'pending',
  PAID: 'paid',
  REFUNDED: 'refunded'
};

// Métodos de pago
export const PAYMENT_METHODS = {
  CASH: 'cash',
  CARD: 'card',
  PAYPAL: 'paypal'
};

// Función para crear un pedido vacío
export const createEmptyOrder = (type = 'local') => ({
  type: type,
  status: 'pending',
  customer: {
    name: '',
    phone: '',
    email: '',
    address: ''
  },
  items: [],
  total: 0,
  table: '',
  date: new Date().toISOString().split('T')[0],
  time: '',
  notes: '',
  paymentMethod: 'cash',
  paymentStatus: 'pending'
});

// Validación básica de pedido
export const validateOrder = (order) => {
  const errors = {};
  
  if (!order.customer.name) errors.customerName = 'El nombre del cliente es obligatorio';
  if (!order.customer.phone) errors.customerPhone = 'El teléfono del cliente es obligatorio';
  
  if (order.type === 'delivery' && !order.customer.address) {
    errors.customerAddress = 'La dirección es obligatoria para pedidos a domicilio';
  }
  
  if (order.items.length === 0) {
    errors.items = 'El pedido debe tener al menos un producto';
  }
  
  if (!order.date || !order.time) {
    errors.datetime = 'La fecha y hora son obligatorias';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Calcular total de un pedido
export const calculateOrderTotal = (items) => {
  return items.reduce((total, item) => total + (item.price * item.quantity), 0);
};

const Orders = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontFamily: theme.typography.h1.fontFamily,
            color: theme.palette.primary.main,
            fontWeight: 700
          }}
        >
          Realiza tu Pedido
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary"
          sx={{ maxWidth: 700, mx: 'auto', mb: 4 }}
        >
          Disfruta de nuestra deliciosa carta como prefieras. Elige entre comer en nuestro restaurante, 
          recoger tu pedido o recibirlo en la comodidad de tu hogar.
        </Typography>
      </Box>

      <Grid container spacing={4} justifyContent="center">
        {/* Opción 1: Pedir en Mesa */}
        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-10px)',
                boxShadow: '0 20px 30px rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <CardMedia
              component="img"
              height="200"
              image="/api/placeholder/600/400"
              alt="Restaurante"
              sx={{ objectFit: 'cover' }}
            />
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RestaurantIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                  Pedir en Mesa
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                Si ya estás en nuestro restaurante, puedes realizar tu pedido desde tu mesa sin necesidad 
                de esperar al camarero.
              </Typography>
              <Box sx={{ mt: 'auto', pt: 2 }}>
                <Button 
                  variant="contained" 
                  fullWidth
                  size="large"
                  onClick={() => navigate('/pedido-mesa')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    py: 1.5, 
                    borderRadius: 2,
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark
                    }
                  }}
                >
                  Pedir Ahora
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Opción 2: Para Recoger */}
        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-10px)',
                boxShadow: '0 20px 30px rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <CardMedia
              component="img"
              height="200"
              image="/api/placeholder/600/400"
              alt="Recoger pedido"
              sx={{ objectFit: 'cover' }}
            />
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StoreIcon color="secondary" sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                  Para Recoger
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                Haz tu pedido online y pasa a recogerlo cuando esté listo. Te avisaremos 
                cuando puedas venir a buscarlo.
              </Typography>
              <Box sx={{ mt: 'auto', pt: 2 }}>
                <Button 
                  variant="contained" 
                  color="secondary"
                  fullWidth
                  size="large"
                  onClick={() => navigate('/pedido-recoger')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    py: 1.5, 
                    borderRadius: 2
                  }}
                >
                  Pedir Para Recoger
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Opción 3: A Domicilio */}
        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-10px)',
                boxShadow: '0 20px 30px rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <CardMedia
              component="img"
              height="200"
              image="/api/placeholder/600/400"
              alt="Entrega a domicilio"
              sx={{ objectFit: 'cover' }}
            />
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HomeIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                  A Domicilio
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                Recibe tu pedido en la comodidad de tu hogar. Nuestro servicio de entrega 
                te llevará los platos directamente a tu puerta.
              </Typography>
              <Box sx={{ mt: 'auto', pt: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={() => navigate('/pedido-domicilio')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    py: 1.5, 
                    borderRadius: 2,
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark
                    }
                  }}
                >
                  Pedir a Domicilio
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sección Adicional - Ver Carta */}
      <Paper 
        elevation={0} 
        sx={{ 
          mt: 6, 
          p: 4, 
          borderRadius: 3, 
          bgcolor: theme.palette.neutral.main,
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" gutterBottom>
          ¿Prefieres solo ver nuestra carta?
        </Typography>
        <Typography variant="body1" paragraph>
          Puedes consultar nuestros platos y precios sin realizar ningún pedido.
        </Typography>
        <Button 
          variant="outlined" 
          size="large"
          onClick={() => navigate('/carta')}
          sx={{ 
            px: 4, 
            py: 1.5, 
            borderRadius: 2
          }}
        >
          Ver Carta Completa
        </Button>
      </Paper>
    </Container>
  );
};

export default Orders;